/**
 * Editor Context
 *
 * Provides access to Monaco editor instance and editor commands
 * for components like the Object Browser that need to interact with the editor.
 */

import { createContext, useContext, useRef, useCallback, useState, type ReactNode } from 'react';
import type { editor } from 'monaco-editor';
import type * as Monaco from 'monaco-editor';
import { useIDFStore } from '../stores/idfStore';
import type { IDFObject } from '../parser';

/**
 * Editor command actions available to external components
 */
export interface EditorCommands {
  /** Navigate to a specific line in the editor */
  navigateToLine: (line: number) => void;

  /** Navigate to an object by its ID */
  navigateToObject: (objectId: string) => void;

  /** Highlight a range in the editor */
  highlightRange: (startLine: number, endLine: number) => void;

  /** Clear all highlights */
  clearHighlights: () => void;

  /** Select a range in the editor */
  selectRange: (startLine: number, startColumn: number, endLine: number, endColumn: number) => void;

  /** Fold lines in the editor */
  foldLines: (startLine: number, endLine: number) => void;

  /** Unfold lines in the editor */
  unfoldLines: (startLine: number, endLine: number) => void;

  /** Fold all objects of a specific class */
  foldAllOfClass: (className: string) => void;

  /** Unfold all objects of a specific class */
  unfoldAllOfClass: (className: string) => void;

  /** Add decorations to highlight all objects of a class */
  highlightClass: (className: string) => void;

  /** Dim all objects except the specified class */
  dimOtherClasses: (className: string) => void;

  /** Clear all class-based decorations */
  clearClassDecorations: () => void;

  /** Select all objects of a class with multi-cursor */
  selectAllOfClass: (className: string) => void;

  /** Copy all objects of a class to clipboard */
  copyAllOfClass: (className: string) => Promise<void>;

  /** Get the editor instance (for advanced operations) */
  getEditor: () => editor.IStandaloneCodeEditor | null;

  /** Get Monaco instance */
  getMonaco: () => typeof Monaco | null;
}

interface EditorContextValue {
  /** Register the Monaco editor instance */
  registerEditor: (
    editorInstance: editor.IStandaloneCodeEditor,
    monacoInstance: typeof Monaco
  ) => void;

  /** Unregister the editor (on unmount) */
  unregisterEditor: () => void;

  /** Editor commands */
  commands: EditorCommands;

  /** Whether editor is registered */
  isEditorReady: boolean;
}

const EditorContext = createContext<EditorContextValue | null>(null);

/**
 * Hook to access editor commands
 */
export function useEditorCommands(): EditorCommands {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditorCommands must be used within an EditorProvider');
  }
  return context.commands;
}

/**
 * Hook to check if editor is ready
 */
export function useEditorReady(): boolean {
  const context = useContext(EditorContext);
  return context?.isEditorReady ?? false;
}

/**
 * Hook to register editor (used by IDFEditor)
 * Returns null functions if not within EditorProvider (graceful degradation)
 */
export function useEditorRegistration() {
  const context = useContext(EditorContext);
  if (!context) {
    // Return no-op functions for graceful degradation
    return {
      registerEditor: () => {},
      unregisterEditor: () => {},
    };
  }
  return {
    registerEditor: context.registerEditor,
    unregisterEditor: context.unregisterEditor,
  };
}

interface EditorProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages editor instance and commands
 */
export function EditorProvider({ children }: EditorProviderProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const classDecorationsRef = useRef<string[]>([]);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const registerEditor = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: typeof Monaco) => {
      editorRef.current = editorInstance;
      monacoRef.current = monacoInstance;
      setIsEditorReady(true);
    },
    []
  );

  const unregisterEditor = useCallback(() => {
    editorRef.current = null;
    monacoRef.current = null;
    setIsEditorReady(false);
  }, []);

  // Helper to get objects by class
  const getObjectsByClass = useCallback((className: string): IDFObject[] => {
    const state = useIDFStore.getState();
    return state.getObjectsByClass(className);
  }, []);

  // Helper to get object by ID
  const getObjectById = useCallback((objectId: string): IDFObject | undefined => {
    const state = useIDFStore.getState();
    return state.getObjectById(objectId);
  }, []);

  const commands: EditorCommands = {
    navigateToLine: useCallback((line: number) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.revealLineInCenter(line);
      editor.setPosition({ lineNumber: line, column: 1 });
      // Don't call focus() on mobile as it can cause issues
      if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        editor.focus();
      }
    }, []),

    navigateToObject: useCallback(
      (objectId: string) => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const object = getObjectById(objectId);
        if (!object?.sourceLocation) return;

        const { startLine, endLine, startColumn = 0, endColumn } = object.sourceLocation;

        // Reveal the object in center
        editor.revealLineInCenter(startLine);

        // Select the entire object
        const model = editor.getModel();
        const endCol = endColumn ?? (model?.getLineMaxColumn(endLine) || 1);

        editor.setSelection({
          startLineNumber: startLine,
          startColumn: startColumn + 1,
          endLineNumber: endLine,
          endColumn: endCol + 1,
        });

        editor.focus();
      },
      [getObjectById]
    ),

    highlightRange: useCallback((startLine: number, endLine: number) => {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return;

      // Clear previous highlights
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);

      // Add new highlight
      decorationsRef.current = editor.deltaDecorations([], [
        {
          range: new monaco.Range(startLine, 1, endLine, 1),
          options: {
            isWholeLine: true,
            className: 'object-highlight-background',
            glyphMarginClassName: 'object-highlight-glyph',
            overviewRuler: {
              color: '#4EC9B0',
              position: monaco.editor.OverviewRulerLane.Full,
            },
            minimap: {
              color: '#4EC9B0',
              position: monaco.editor.MinimapPosition.Inline,
            },
          },
        },
      ]);
    }, []),

    clearHighlights: useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
    }, []),

    selectRange: useCallback(
      (startLine: number, startColumn: number, endLine: number, endColumn: number) => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.setSelection({
          startLineNumber: startLine,
          startColumn,
          endLineNumber: endLine,
          endColumn,
        });
        editor.revealLineInCenter(startLine);
        editor.focus();
      },
      []
    ),

    foldLines: useCallback((startLine: number, endLine: number) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.setSelection({
        startLineNumber: startLine,
        startColumn: 1,
        endLineNumber: endLine,
        endColumn: 1,
      });
      editor.trigger('fold', 'editor.fold', {});
    }, []),

    unfoldLines: useCallback((startLine: number, endLine: number) => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.setSelection({
        startLineNumber: startLine,
        startColumn: 1,
        endLineNumber: endLine,
        endColumn: 1,
      });
      editor.trigger('unfold', 'editor.unfold', {});
    }, []),

    foldAllOfClass: useCallback(
      (className: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const objects = getObjectsByClass(className);
        for (const obj of objects) {
          if (obj.sourceLocation) {
            const { startLine, endLine } = obj.sourceLocation;
            editor.setSelection({
              startLineNumber: startLine,
              startColumn: 1,
              endLineNumber: endLine,
              endColumn: 1,
            });
            editor.trigger('fold', 'editor.fold', {});
          }
        }
      },
      [getObjectsByClass]
    ),

    unfoldAllOfClass: useCallback(
      (className: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const objects = getObjectsByClass(className);
        for (const obj of objects) {
          if (obj.sourceLocation) {
            const { startLine, endLine } = obj.sourceLocation;
            editor.setSelection({
              startLineNumber: startLine,
              startColumn: 1,
              endLineNumber: endLine,
              endColumn: 1,
            });
            editor.trigger('unfold', 'editor.unfold', {});
          }
        }
      },
      [getObjectsByClass]
    ),

    highlightClass: useCallback(
      (className: string) => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const objects = getObjectsByClass(className);
        const decorations: editor.IModelDeltaDecoration[] = objects
          .filter((obj) => obj.sourceLocation)
          .map((obj) => ({
            range: new monaco.Range(
              obj.sourceLocation!.startLine,
              1,
              obj.sourceLocation!.endLine,
              1
            ),
            options: {
              isWholeLine: true,
              className: 'class-highlight-background',
              overviewRuler: {
                color: '#4EC9B0',
                position: monaco.editor.OverviewRulerLane.Full,
              },
              minimap: {
                color: '#4EC9B0',
                position: monaco.editor.MinimapPosition.Inline,
              },
            },
          }));

        // Clear previous class decorations and add new ones
        classDecorationsRef.current = editor.deltaDecorations(
          classDecorationsRef.current,
          decorations
        );
      },
      [getObjectsByClass]
    ),

    dimOtherClasses: useCallback(
      (className: string) => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const state = useIDFStore.getState();
        const allObjects = state.document?.objects ?? [];

        const decorations: editor.IModelDeltaDecoration[] = allObjects
          .filter(
            (obj) =>
              obj.className.toLowerCase() !== className.toLowerCase() && obj.sourceLocation
          )
          .map((obj) => ({
            range: new monaco.Range(
              obj.sourceLocation!.startLine,
              1,
              obj.sourceLocation!.endLine,
              1
            ),
            options: {
              isWholeLine: true,
              className: 'dimmed-content',
              inlineClassName: 'dimmed-text',
            },
          }));

        classDecorationsRef.current = editor.deltaDecorations(
          classDecorationsRef.current,
          decorations
        );
      },
      []
    ),

    clearClassDecorations: useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;
      classDecorationsRef.current = editor.deltaDecorations(classDecorationsRef.current, []);
    }, []),

    selectAllOfClass: useCallback(
      (className: string) => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const objects = getObjectsByClass(className);
        if (objects.length === 0) return;

        const model = editor.getModel();
        if (!model) return;

        // Create selections for all objects of this class
        const selections = objects
          .filter((obj) => obj.sourceLocation)
          .map((obj) => {
            const { startLine, endLine, startColumn = 0, endColumn } = obj.sourceLocation!;
            const endCol = endColumn ?? model.getLineMaxColumn(endLine);
            return {
              selectionStartLineNumber: startLine,
              selectionStartColumn: startColumn + 1,
              positionLineNumber: endLine,
              positionColumn: endCol + 1,
            };
          });

        const firstSelection = selections[0];
        if (firstSelection) {
          editor.setSelections(selections);
          // Navigate to first object
          editor.revealLineInCenter(firstSelection.selectionStartLineNumber);
          editor.focus();
        }
      },
      [getObjectsByClass]
    ),

    copyAllOfClass: useCallback(
      async (className: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const objects = getObjectsByClass(className);
        if (objects.length === 0) return;

        const model = editor.getModel();
        if (!model) return;

        // Extract text for all objects of this class
        const textParts: string[] = objects
          .filter((obj) => obj.sourceLocation)
          .map((obj) => {
            const { startLine, endLine, startColumn = 0, endColumn } = obj.sourceLocation!;
            const endCol = endColumn ?? model.getLineMaxColumn(endLine);
            return model.getValueInRange({
              startLineNumber: startLine,
              startColumn: startColumn + 1,
              endLineNumber: endLine,
              endColumn: endCol + 1,
            });
          });

        const text = textParts.join('\n\n');

        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // Fallback: use execCommand
          const textarea = document.createElement('textarea');
          textarea.value = text;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
      },
      [getObjectsByClass]
    ),

    getEditor: useCallback(() => editorRef.current, []),

    getMonaco: useCallback(() => monacoRef.current, []),
  };

  return (
    <EditorContext.Provider
      value={{
        registerEditor,
        unregisterEditor,
        commands,
        isEditorReady,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
}
